import { useState, useMemo, useCallback, useRef, useEffect, memo } from 'react';
import {
    ReactFlow, Background, Controls,
    Handle, Position, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FrameContent, Tokens, BindingsLine } from './PrologComponents';
import { renderTerm, renderTermExplicit } from './prologRenderer';

// ── Layout constants ─────────────────────────────────────────────────────────
const CONJ_W     = 48;   // must match .gn-diamond-wrapper { width } in CSS
const H_GAP      = 40;   // minimum gap between node edges at the same depth
const V_STEP     = 108;  // vertical distance between depth levels
const PAD_X      = 40;   // left padding applied after layout
const MIN_GOAL_W = 140;
const GOAL_PAD_H = 22;   // left + right CSS padding + border (px)

// ── Text measurement ─────────────────────────────────────────────────────────

let _measureCtx = null;
function textPx(str) {
    if (!_measureCtx) {
        _measureCtx = document.createElement('canvas').getContext('2d');
        _measureCtx.font = '13px monospace';
    }
    return _measureCtx.measureText(str).width;
}

function tokenStr(tokens) {
    return tokens.map(t => t.text).join('');
}

function measureGoalWidth(frame, termVec, idVec, varIdVec, showBindings) {
    if (!termVec || !idVec || !varIdVec) return MIN_GOAL_W;
    let mainPx, bindingsPx = 0;
    if (showBindings) {
        const bindings = new Map();
        const { tokens } = renderTermExplicit(frame.index, termVec, idVec, varIdVec, bindings);
        mainPx = textPx(tokenStr(tokens));
        if (bindings.size) {
            let bText = '';
            let i = 0;
            for (const { nameTokens, valueTokens } of bindings.values()) {
                if (i++ > 0) bText += ', ';
                bText += tokenStr(nameTokens) + ' = ' + tokenStr(valueTokens);
            }
            bindingsPx = textPx(bText);
        }
    } else {
        mainPx = textPx(tokenStr(renderTerm(frame.index, termVec, idVec, varIdVec)));
    }
    return Math.max(MIN_GOAL_W, Math.ceil(Math.max(mainPx, bindingsPx)) + GOAL_PAD_H);
}

// ── Reingold-Tilford layout ───────────────────────────────────────────────────
//
// Extents are stored as two parallel arrays (extL, extR) instead of [l,r] pair
// objects — half as many allocations in the hot path.  The accumulated right
// contour is copied ONCE per parent (from the first child) and then mutated
// in-place for each subsequent sibling, eliminating the per-sibling array copy
// that made the original O(n²) in practice.  Children arrays are already in
// ascending index order (frames are pushed in order), so the sort is skipped.

function computeLayout(stack, nodeWidths) {
    const n = stack.length;
    if (!n) return new Map();

    // Build children lists. Frames are added in index order so each list is
    // already sorted ascending — no explicit sort needed.
    const children = [];
    let root = -1;
    for (let i = 0; i < n; i++) children.push([]);
    for (let i = 0; i < n; i++) {
        const p = stack[i].parent;
        if (p === -1) root = i;
        else children[p].push(i);
    }
    if (root === -1) return new Map();

    const relX = new Float64Array(n); // x relative to parent centre
    const extL = new Array(n);        // left  contour per depth level
    const extR = new Array(n);        // right contour per depth level

    function postOrder(i) {
        const kids = children[i];
        const hw   = nodeWidths[i] / 2;

        if (!kids.length) {
            extL[i] = [-hw];
            extR[i] = [ hw];
            return;
        }

        for (const k of kids) postOrder(k);

        // Copy kids[0]'s right contour as the starting accumulated contour;
        // subsequent siblings merge into it in-place (no further copies).
        const offsets = new Array(kids.length);
        offsets[0] = 0;
        const rRight = extR[kids[0]].slice();

        for (let j = 1; j < kids.length; j++) {
            const lLeft = extL[kids[j]];
            let shift = 0;
            const minLen = rRight.length < lLeft.length ? rRight.length : lLeft.length;
            for (let d = 0; d < minLen; d++) {
                const gap = rRight[d] - lLeft[d] + H_GAP;
                if (gap > shift) shift = gap;
            }
            offsets[j] = shift;
            const kr = extR[kids[j]];
            for (let d = 0; d < kr.length; d++) {
                const r = kr[d] + shift;
                if (d < rRight.length) { if (r > rRight[d]) rRight[d] = r; }
                else rRight.push(r);
            }
        }

        const centre = (offsets[0] + offsets[kids.length - 1]) / 2;
        for (let j = 0; j < kids.length; j++) relX[kids[j]] = offsets[j] - centre;

        const myL = [-hw];
        const myR = [ hw];
        for (let j = 0; j < kids.length; j++) {
            const k  = kids[j];
            const ko = relX[k];
            const kl = extL[k];
            const kr = extR[k];
            for (let d = 0; d < kl.length; d++) {
                const dl = kl[d] + ko;
                const dr = kr[d] + ko;
                const d1 = d + 1;
                if (d1 >= myL.length) { myL.push(dl); myR.push(dr); }
                else {
                    if (dl < myL[d1]) myL[d1] = dl;
                    if (dr > myR[d1]) myR[d1] = dr;
                }
            }
            extL[k] = null; // release child extent once merged
            extR[k] = null;
        }
        extL[i] = myL;
        extR[i] = myR;
    }

    postOrder(root);

    const posX = new Float64Array(n);
    const posY = new Float64Array(n);

    function preOrder(i, cx, depth) {
        posX[i] = cx - nodeWidths[i] / 2;
        posY[i] = depth * V_STEP;
        for (const k of children[i]) preOrder(k, cx + relX[k], depth + 1);
    }
    preOrder(root, 0, 0);

    let minX = Infinity;
    for (let i = 0; i < n; i++) { if (posX[i] < minX) minX = posX[i]; }
    const dx = PAD_X - minX;
    for (let i = 0; i < n; i++) posX[i] += dx;

    const positions = new Map();
    for (let i = 0; i < n; i++) positions.set(i, { x: posX[i], y: posY[i] });
    return positions;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Cut has its own frame_type::CUT in the backend — serialised as 'CUT'.
function isCutFrame(frame) {
    return frame.type === 'CUT';
}

// ── Custom node components ───────────────────────────────────────────────────

function GoalNode({ data }) {
    const { frame, termVec, idVec, varIdVec, isActive, showBindings, nodeState, choicePoint, isNegation } = data;

    // In explicit mode we render here directly (not via FrameContent) so the
    // choice-point indicator always sits on the same line as the term, with
    // the bindings line appearing below as a separate block.
    let bindingsContent = null;
    let termContent;
    if (showBindings && termVec && idVec && varIdVec) {
        const bindings = new Map();
        const { tokens } = renderTermExplicit(frame.index, termVec, idVec, varIdVec, bindings);
        termContent = <Tokens tokens={tokens} />;
        if (bindings.size) bindingsContent = <BindingsLine bindings={[...bindings.values()]} />;
    } else {
        termContent = (
            <FrameContent
                frame={frame} termVec={termVec} idVec={idVec} varIdVec={varIdVec}
                isActive={isActive} showBindings={false}
            />
        );
    }

    const cpDotClass = choicePoint?.type === 'NEGATION'
        ? 'gn-choice-dot gn-choice-negation'
        : 'gn-choice-dot';

    return (
        <div className={`gn-goal gn-state-${nodeState}${isNegation ? ' gn-negation' : ''}`}>
            <Handle type="target" position={Position.Top} style={{ opacity: 0 }} isConnectable={false} />
            <div className="gn-goal-body">
                <div className="gn-term-line">
                    {termContent}
                    {choicePoint !== undefined && (
                        <span className={cpDotClass}>
                            {choicePoint.type === 'NEGATION' ? '◆' : `◆${choicePoint.decisionIndex}`}
                        </span>
                    )}
                </div>
                {bindingsContent}
            </div>
            <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} isConnectable={false} />
        </div>
    );
}

// Resets the viewport to centre the tree whenever a new query starts.
// Must live inside <ReactFlow> so it can call useReactFlow().
function ViewportReset({ queryKey }) {
    const { fitView } = useReactFlow();
    useEffect(() => {
        // Let React Flow finish measuring nodes before fitting.
        const id = setTimeout(() => fitView({ padding: 0.15, maxZoom: 0.75, duration: 200 }), 50);
        return () => clearTimeout(id);
    // fitView is stable; we intentionally only re-run on queryKey changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey]);
    return null;
}

// Handles inset to the diamond tips: the wrapper is 48 px, the diamond is 40 px
// centered inside it (4 px padding each side), so the pointed tips sit at y=4
// (top) and y=44 (bottom) within the wrapper.  Offsetting the handles by 4 px
// makes React Flow draw edges that terminate exactly at the visual tips.
const DIAMOND_HANDLE_TOP    = { opacity: 0, top:    4 };
const DIAMOND_HANDLE_BOTTOM = { opacity: 0, bottom: 4 };

function ConjNode({ data }) {
    const [hovered, setHovered] = useState(false);
    const { frame, termVec, idVec, varIdVec, isActive, showBindings, nodeState, choicePoint } = data;
    const cpClass = choicePoint?.type === 'NEGATION'
        ? 'gn-diamond-choice gn-choice-negation' : 'gn-diamond-choice';
    return (
        <div
            className="gn-diamond-wrapper"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Handle type="target" position={Position.Top} style={DIAMOND_HANDLE_TOP} isConnectable={false} />
            <div className={`gn-diamond gn-conj-diamond gn-state-${nodeState}`}>
                <span className="gn-diamond-sym">∧</span>
            </div>
            {choicePoint !== undefined && (
                <span className={cpClass}>
                    {choicePoint.type === 'NEGATION' ? '◆' : `◆${choicePoint.decisionIndex}`}
                </span>
            )}
            {hovered && (
                <div className="gn-tooltip">
                    <FrameContent
                        frame={frame} termVec={termVec} idVec={idVec} varIdVec={varIdVec}
                        isActive={isActive} showBindings={showBindings}
                    />
                </div>
            )}
            <Handle type="source" position={Position.Bottom} style={DIAMOND_HANDLE_BOTTOM} isConnectable={false} />
        </div>
    );
}

function DisjNode({ data }) {
    const [hovered, setHovered] = useState(false);
    const { frame, termVec, idVec, varIdVec, isActive, showBindings, nodeState, choicePoint } = data;
    const cpClass = choicePoint?.type === 'NEGATION'
        ? 'gn-diamond-choice gn-choice-negation' : 'gn-diamond-choice';
    return (
        <div
            className="gn-diamond-wrapper"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Handle type="target" position={Position.Top} style={DIAMOND_HANDLE_TOP} isConnectable={false} />
            <div className={`gn-diamond gn-disj-diamond gn-state-${nodeState}`}>
                <span className="gn-diamond-sym">∨</span>
            </div>
            {choicePoint !== undefined && (
                <span className={cpClass}>
                    {choicePoint.type === 'NEGATION' ? '◆' : `◆${choicePoint.decisionIndex}`}
                </span>
            )}
            {hovered && (
                <div className="gn-tooltip">
                    <FrameContent
                        frame={frame} termVec={termVec} idVec={idVec} varIdVec={varIdVec}
                        isActive={isActive} showBindings={showBindings}
                    />
                </div>
            )}
            <Handle type="source" position={Position.Bottom} style={DIAMOND_HANDLE_BOTTOM} isConnectable={false} />
        </div>
    );
}

function CutNode({ data }) {
    const [hovered, setHovered] = useState(false);
    const { nodeState, choicePoint } = data;
    const cpClass = choicePoint?.type === 'NEGATION'
        ? 'gn-diamond-choice gn-choice-negation' : 'gn-diamond-choice';
    return (
        <div
            className="gn-diamond-wrapper"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Handle type="target" position={Position.Top} style={DIAMOND_HANDLE_TOP} isConnectable={false} />
            <div className={`gn-diamond gn-cut-diamond gn-state-${nodeState}`}>
                <span className="gn-diamond-sym">!</span>
            </div>
            {choicePoint !== undefined && (
                <span className={cpClass}>
                    {choicePoint.type === 'NEGATION' ? '◆' : `◆${choicePoint.decisionIndex}`}
                </span>
            )}
            {hovered && (
                <div className="gn-tooltip" style={{ borderColor: '#9d5cc4' }}>
                    cut
                </div>
            )}
            <Handle type="source" position={Position.Bottom} style={DIAMOND_HANDLE_BOTTOM} isConnectable={false} />
        </div>
    );
}

// Defined at module scope — stable reference, no React Flow recreation warnings.
const nodeTypes = { goalNode: GoalNode, conjNode: ConjNode, disjNode: DisjNode, cutNode: CutNode };

// ── Main component ───────────────────────────────────────────────────────────

export const GraphView = memo(function GraphView({
    display,
    grayOut,
    fullscreen,
    idVec,
    varIdVec,
    showBindings,
    activeChain,
    choicePointMap,
    inactiveFrames,
    queryKey,
    lightMode,
}) {
    // Measured DOM widths captured after React Flow renders each node.
    // Used by the layout algorithm so positions match actual rendered sizes.
    const measuredWidthsRef = useRef(new Map());
    const [layoutVersion, setLayoutVersion] = useState(0);

    // Clear measured widths when content changes enough to invalidate old measurements.
    useEffect(() => {
        measuredWidthsRef.current = new Map();
    }, [queryKey, showBindings]);

    // React Flow fires onNodesChange with type:'dimensions' after measuring each node.
    // Mutate the map in-place — the ref doesn't need a new reference for React to
    // pick up the change; only the layoutVersion state increment triggers re-render.
    const handleNodesChange = useCallback((changes) => {
        let dirty = false;
        const map = measuredWidthsRef.current;
        for (const c of changes) {
            if (c.type !== 'dimensions') continue;
            const w = c.dimensions?.width;
            if (!w) continue;
            if (map.get(c.id) !== w) {
                map.set(c.id, w);
                dirty = true;
            }
        }
        if (dirty) setLayoutVersion(v => v + 1);
    }, []);

    const { rfNodes, rfEdges } = useMemo(() => {
        if (!display) return { rfNodes: [], rfEdges: [] };

        const stack = display.stack;

        // For each node: use the DOM-measured width when available (accurate, includes
        // choice-point indicator and any other rendered content), falling back to the
        // canvas estimate for nodes that haven't been measured yet.
        const nodeWidths = new Array(stack.length);
        for (let i = 0; i < stack.length; i++) {
            const frame = stack[i];
            if (frame.type === 'CONJUNCTION' || frame.type === 'DISJUNCTION' ||
                    isCutFrame(frame)) {
                nodeWidths[i] = CONJ_W;
            } else {
                const measured = measuredWidthsRef.current.get(String(i));
                nodeWidths[i] = measured ?? measureGoalWidth(frame, display.termVec, idVec, varIdVec, showBindings);
            }
        }

        const positions = computeLayout(stack, nodeWidths);
        const rfNodes = [];
        const rfEdges = [];

        for (let i = 0; i < stack.length; i++) {
            const frame = stack[i];
            const pos = positions.get(i);
            if (!pos) continue;

            const isActive  = i === display.stackPointer;
            const inChain   = activeChain.has(i);
            const isSuccess = display.solutionFound && inChain;

            let nodeState;
            if      (isActive)  nodeState = 'active';
            else if (isSuccess) nodeState = 'success';
            else if (inChain)   nodeState = 'chain';
            else                nodeState = 'inactive';

            const hidden = inactiveFrames === 'hide' && !inChain;
            if (hidden) continue;

            const dimmed = inactiveFrames === 'dim' && !inChain;

            const isCut = isCutFrame(frame);
            const nodeType = frame.type === 'CONJUNCTION' ? 'conjNode'
                           : frame.type === 'DISJUNCTION' ? 'disjNode'
                           : isCut                        ? 'cutNode'
                           : 'goalNode';

            // Detect negation built-ins (not/1 or \+/1) by inspecting the term.
            let isNegation = false;
            if (frame.type === 'RULE' && display.termVec && idVec) {
                const term = display.termVec[frame.index];
                if (term?.termType === 'STRUCTURE' && term.children.length === 1) {
                    const name = idVec[term.identifierIdx];
                    isNegation = name === 'not' || name === '\\+';
                }
            }

            const choicePoint = choicePointMap.has(i) ? choicePointMap.get(i) : undefined;

            const nodeStyle = dimmed ? { opacity: 0.3 } : undefined;

            rfNodes.push({
                id:         String(i),
                type:       nodeType,
                position:   pos,
                draggable:  false,
                selectable: false,
                style:      nodeStyle,
                data: {
                    frame,
                    termVec: display.termVec,
                    idVec,
                    varIdVec,
                    isActive,
                    showBindings,
                    nodeState,
                    choicePoint,
                    isNegation,
                },
            });

            if (frame.parent !== -1 && !(inactiveFrames === 'hide' && !activeChain.has(frame.parent))) {
                const bothInChain = inChain && activeChain.has(frame.parent);
                rfEdges.push({
                    id:     `e${frame.parent}-${i}`,
                    source: String(frame.parent),
                    target: String(i),
                    type:   'default',
                    style: {
                        stroke:      bothInChain
                            ? (lightMode ? '#0e639c' : '#569cd6')
                            : (lightMode ? '#c0c0c0' : '#3a3a3a'),
                        strokeWidth: bothInChain ? 2 : 1,
                        opacity:     dimmed ? 0.3 : 1,
                    },
                });
            }
        }

        if (grayOut) {
            return {
                rfNodes: rfNodes.map(n => ({
                    ...n,
                    style: { ...(n.style ?? {}), opacity: 0.25 },
                    data:  { ...n.data, nodeState: 'inactive' },
                })),
                rfEdges: rfEdges.map(e => ({
                    ...e,
                    style: { stroke: lightMode ? '#c0c0c0' : '#3a3a3a', strokeWidth: 1, opacity: 0.25 },
                })),
            };
        }

        return { rfNodes, rfEdges };
    }, [display, grayOut, idVec, varIdVec, showBindings, activeChain, choicePointMap, inactiveFrames, layoutVersion, lightMode]);

    const containerClass = [
        'graph-view-container',
        fullscreen  ? 'graph-view-fullscreen' : '',
        lightMode   ? 'graph-view-light'      : '',
    ].filter(Boolean).join(' ');

    if (!display) {
        return <div className={`${containerClass} graph-view-empty`}>Run a query to see the execution graph.</div>;
    }

    return (
        <div className={containerClass}>
            <ReactFlow
                nodes={rfNodes}
                edges={rfEdges}
                nodeTypes={nodeTypes}
                onNodesChange={handleNodesChange}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                panOnDrag={true}
                zoomOnScroll={true}
                defaultViewport={{ x: window.innerWidth / 2 - 82, y: 40, zoom: 0.75 }}
                minZoom={0.1}
                maxZoom={4}
                deleteKeyCode={null}
                selectionKeyCode={null}
                multiSelectionKeyCode={null}
                zoomActivationKeyCode={null}
                panActivationKeyCode={null}
            >
                <ViewportReset queryKey={queryKey} />
                <Background
                    color={lightMode ? '#d8d8d8' : '#2a2a2a'}
                    gap={20}
                    size={1}
                />
                <Controls showInteractive={false} position="bottom-right" />
            </ReactFlow>
        </div>
    );
});
